import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { useOrgStore } from '@/store/orgStore';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Phone, Calendar, CheckSquare } from 'lucide-react';

const typeIcons: Record<string, any> = { llamada: Phone, reunion: Calendar, tarea: CheckSquare };
const typeLabels: Record<string, string> = { llamada: 'Llamada', reunion: 'Reunion', tarea: 'Tarea' };
const statusColors: Record<string, string> = {
  pendiente: 'bg-[#FFFBEB] text-[#B45309] border-[#F59E0B]/20',
  completada: 'bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20',
  cancelada: 'bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/20',
};
const emptyActivity = { type: 'tarea', subject: '', description: '', due_date: '', status: 'pendiente', company_id: '', contact_id: '', opportunity_id: '', related_to: '', assigned_to: '' };

export default function ActivitiesPage() {
  const [activities, setActivities] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [form, setForm] = useState(emptyActivity);
  const [saving, setSaving] = useState(false);
  const { currentOrgId } = useOrgStore();

  const fetchData = useCallback(async () => {
    if (!currentOrgId) return;
    const params: any = {};
    if (search) params.search = search;
    if (typeFilter) params.type = typeFilter;
    if (statusFilter) params.status = statusFilter;
    const [actsRes, compsRes, contsRes, oppsRes] = await Promise.all([
      api.get('/activities', { params }),
      api.get('/companies'),
      api.get('/contacts'),
      api.get('/opportunities'),
    ]);
    setActivities(actsRes.data);
    setCompanies(compsRes.data);
    setContacts(contsRes.data);
    setOpportunities(oppsRes.data);
  }, [currentOrgId, search, typeFilter, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getCompanyName = (id: string) => companies.find(c => c.id === id)?.name || '';
  const getOppName = (id: string) => opportunities.find(o => o.id === id)?.name || '';
  const filteredContacts = form.company_id ? contacts.filter(c => c.company_id === form.company_id) : contacts;
  const filteredOpps = form.company_id ? opportunities.filter(o => o.account_id === form.company_id) : opportunities;

  const openCreate = () => { setEditItem(null); setForm(emptyActivity); setDialogOpen(true); };
  const openEdit = (item: any) => {
    setEditItem(item);
    setForm({ type: item.type, subject: item.subject, description: item.description, due_date: item.due_date, status: item.status, company_id: item.company_id || '', contact_id: item.contact_id || '', opportunity_id: item.opportunity_id || '', related_to: item.related_to, assigned_to: item.assigned_to });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editItem) { await api.put(`/activities/${editItem.id}`, form); toast.success('Actividad actualizada'); }
      else { await api.post('/activities', form); toast.success('Actividad creada'); }
      setDialogOpen(false); fetchData();
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await api.delete(`/activities/${deleteId}`); toast.success('Actividad eliminada'); setDeleteId(null); fetchData(); }
    catch { toast.error('Error al eliminar'); }
  };

  return (
    <div data-testid="activities-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Actividades</h1>
          <p className="text-sm text-[#475569] mt-1">{activities.length} actividades registradas</p>
        </div>
        <Button onClick={openCreate} className="w-full sm:w-auto min-h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold" data-testid="create-activity-btn">
          <Plus size={16} className="mr-2" /> Nueva Actividad
        </Button>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input placeholder="Buscar actividades..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 border-[#E2E8F0]" data-testid="activities-search" />
        </div>
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36 border-[#E2E8F0]"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="tarea">Tarea</SelectItem>
            <SelectItem value="llamada">Llamada</SelectItem>
            <SelectItem value="reunion">Reunion</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36 border-[#E2E8F0]"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pendiente">Pendiente</SelectItem>
            <SelectItem value="completada">Completada</SelectItem>
            <SelectItem value="cancelada">Cancelada</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="hidden lg:block bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left" data-testid="activities-table">
            <thead><tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Tipo</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Asunto</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden md:table-cell">Empresa</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden md:table-cell">Oportunidad</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden lg:table-cell">Fecha</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Estado</th>
              <th className="py-3 px-4 w-12"></th>
            </tr></thead>
            <tbody>
              {activities.map((a) => {
                const Icon = typeIcons[a.type] || CheckSquare;
                return (
                  <tr key={a.id} className="border-b border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors">
                    <td className="py-3 px-4"><div className="flex items-center gap-2"><Icon size={16} className="text-[#2563EB]" /><span className="text-[#475569]">{typeLabels[a.type] || a.type}</span></div></td>
                    <td className="py-3 px-4 font-medium text-[#0F172A]">{a.subject}</td>
                    <td className="py-3 px-4 text-[#475569] hidden md:table-cell text-xs">{getCompanyName(a.company_id) || a.related_to || '-'}</td>
                    <td className="py-3 px-4 text-[#475569] hidden md:table-cell text-xs">{getOppName(a.opportunity_id) || '-'}</td>
                    <td className="py-3 px-4 text-[#475569] hidden lg:table-cell">{a.due_date}</td>
                    <td className="py-3 px-4"><Badge variant="outline" className={`text-xs font-medium capitalize ${statusColors[a.status] || ''}`}>{a.status}</Badge></td>
                    <td className="py-3 px-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(a)}><Pencil size={14} className="mr-2" /> Editar</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteId(a.id)} className="text-[#EF4444]"><Trash2 size={14} className="mr-2" /> Eliminar</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
              {activities.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-[#94A3B8]">No hay actividades</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <ul className="lg:hidden space-y-3" data-testid="activities-card-list">
        {activities.map((a) => {
          const Icon = typeIcons[a.type] || CheckSquare;
          return (
            <li key={a.id} className="rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon size={16} className="text-[#2563EB] shrink-0" />
                    <span className="font-semibold text-[#0F172A] truncate">{a.subject}</span>
                  </div>
                  <p className="text-xs text-[#475569] mt-0.5">{typeLabels[a.type] || a.type}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className={`text-xs font-medium capitalize ${statusColors[a.status] || ''}`}>{a.status}</Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-11 w-11"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(a)}><Pencil size={14} className="mr-2" /> Editar</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteId(a.id)} className="text-[#EF4444]"><Trash2 size={14} className="mr-2" /> Eliminar</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-y-1.5 gap-x-3 text-sm">
                <dt className="text-[#64748B]">Empresa</dt>
                <dd className="text-right text-xs text-[#475569] truncate">{getCompanyName(a.company_id) || a.related_to || '-'}</dd>
                <dt className="text-[#64748B]">Oportunidad</dt>
                <dd className="text-right text-xs text-[#475569] truncate">{getOppName(a.opportunity_id) || '-'}</dd>
                <dt className="text-[#64748B]">Fecha</dt>
                <dd className="text-right text-xs text-[#475569] tabular-nums">{a.due_date || '-'}</dd>
              </dl>
            </li>
          );
        })}
        {activities.length === 0 && (
          <li className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8FAFC] p-8 text-center text-sm text-[#94A3B8]">
            No hay actividades
          </li>
        )}
      </ul>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="activity-dialog">
          <DialogHeader><DialogTitle className="font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editItem ? 'Editar Actividad' : 'Nueva Actividad'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="border-[#E2E8F0]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tarea">Tarea</SelectItem>
                    <SelectItem value="llamada">Llamada</SelectItem>
                    <SelectItem value="reunion">Reunion</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="border-[#E2E8F0]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="completada">Completada</SelectItem>
                    <SelectItem value="cancelada">Cancelada</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Asunto</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="border-[#E2E8F0]" data-testid="activity-subject-input" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Descripcion</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border-[#E2E8F0] min-h-[60px]" /></div>
            <div className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg space-y-3">
              <span className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Relacion</span>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#475569]">Empresa</Label>
                  <Select value={form.company_id || "none"} onValueChange={(v) => setForm({ ...form, company_id: v === "none" ? "" : v, contact_id: "", opportunity_id: "" })}>
                    <SelectTrigger className="border-[#E2E8F0]"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin empresa</SelectItem>
                      {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-xs text-[#475569]">Contacto</Label>
                  <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="border-[#E2E8F0]"><SelectValue placeholder={form.company_id ? "Seleccionar" : "Elija empresa"} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin contacto</SelectItem>
                      {filteredContacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              </div>
              <div><Label className="text-xs text-[#475569]">Oportunidad</Label>
                <Select value={form.opportunity_id || "none"} onValueChange={(v) => setForm({ ...form, opportunity_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="border-[#E2E8F0]"><SelectValue placeholder="Seleccionar oportunidad" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin oportunidad</SelectItem>
                    {filteredOpps.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Fecha</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="border-[#E2E8F0]" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E2E8F0]">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || !form.subject} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="activity-save-btn">{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Eliminar Actividad</AlertDialogTitle>
          <AlertDialogDescription>Esta accion no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#EF4444] hover:bg-[#DC2626] text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
